import { Injectable, inject, signal, effect } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API_BASE } from '../../constants/general.constants';
import { AppStateService } from '../app-state/app-state.service';
import {
  ApiReference,
  ApiMethod,
  AutocompleteItem,
  ScoredAutocompleteItem,
  ParamHint,
} from './api-reference.models';

@Injectable({ providedIn: 'root' })
export class ApiReferenceService {
  private readonly http = inject(HttpClient);
  private readonly appState = inject(AppStateService);
  private readonly cache = signal<ApiReference | null>(null);
  private lastKey = '';

  // Per-language autocomplete item caches
  private itemsCacheJs: AutocompleteItem[] | null = null;
  private itemsCachePy: AutocompleteItem[] | null = null;
  private itemsCacheRef: ApiReference | null = null;

  constructor() {
    effect(() => {
      // Reading these signals registers them as dependencies
      const pid = this.appState.projectId();
      const vid = this.appState.versionId();
      const versionProjectId = this.appState.version()?.projectId;
      if (pid && vid && versionProjectId === pid) {
        this.load();
      }
    });
  }

  /**
   * Fetch the API reference for the current project/version.
   * Called automatically via effect() when project or version changes.
   * Safe to call manually — idempotent when key hasn't changed.
   */
  load(): void {
    const projectId = this.appState.projectId();
    const versionId = this.appState.versionId();
    if (!projectId || !versionId) return;
    // While a project is being opened the state can briefly pair the new
    // project with the previous project's version: the guard's
    // refreshProjectData()/tryRestoreProjectFromStorage() set the project
    // before loadProject() commits its version. That pair 404s (EHQ-539), so
    // wait for the version that belongs to this project.
    if (this.appState.version()?.projectId !== projectId) return;

    const key = `${projectId}:${versionId}`;
    if (key === this.lastKey && this.cache()) return;
    this.lastKey = key;
    this.cache.set(null);
    this.itemsCacheJs = null;
    this.itemsCachePy = null;

    this.http
      .get<ApiReference>(
        `${API_BASE}/project/${projectId}/v/${versionId}/api-reference`
      )
      .subscribe({
        next: (data) => this.cache.set(data),
        error: () => {
          this.cache.set(null);
          this.itemsCacheJs = null;
          this.itemsCachePy = null;
        },
      });
  }

  /**
   * Return filtered/scored completions from the cached API reference.
   *
   * @param prefix  The text typed so far (e.g. "ehq.save", "gTR", "store.")
   * @param language 'python' or 'javascript' -- controls which method name to show
   */
  getCompletions(
    prefix: string,
    language: 'python' | 'javascript'
  ): ScoredAutocompleteItem[] {
    const items = this.getCachedItems(language);
    if (items.length === 0) return [];

    // Detect namespace prefix (e.g. "ehq.", "store.", "EHQ.")
    const dotIndex = prefix.lastIndexOf('.');
    let searchText: string;
    let namespaceFilter: string | null = null;

    if (dotIndex >= 0) {
      namespaceFilter = prefix.slice(0, dotIndex).toLowerCase();
      searchText = prefix.slice(dotIndex + 1);
    } else {
      searchText = prefix;
    }

    const results: ScoredAutocompleteItem[] = [];

    for (const item of items) {
      // Filter by namespace if detected.
      // Normalize hyphens/underscores so "demo-arrow", "demo_arrow", and "demoArrow" all match.
      if (namespaceFilter) {
        const normalizedFilter = namespaceFilter.replaceAll('-', '').replaceAll('_', '').toLowerCase();
        const normalizedNs = item.namespace.replaceAll('-', '').replaceAll('_', '').toLowerCase();
        if (normalizedNs !== normalizedFilter) {
          continue;
        }
      }

      const score = this.fuzzyScore(searchText, item.text);
      if (score > 0) {
        results.push({ ...item, score });
      }
    }

    // Sort by score descending, then alphabetically
    results.sort((a, b) => b.score - a.score || a.text.localeCompare(b.text));
    return results;
  }

  /** Return full method detail for the documentation panel. */
  getMethodDetail(namespace: string, methodName: string): ApiMethod | null {
    const ref = this.cache();
    if (!ref) return null;

    const nsLower = namespace.toLowerCase();
    const ns = ref.namespaces.find(
      (n) => n.name.toLowerCase() === nsLower
        || (n.jsName && n.jsName.toLowerCase() === nsLower)
    );
    if (!ns) return null;

    return (
      ns.methods.find((m) =>
        m.name === methodName
          || this.stripNamespacePrefix(m.displayName) === methodName
          || this.stripNamespacePrefix(m.pythonName) === methodName
      ) ?? null
    );
  }

  /**
   * Return the paramHint for a specific parameter of a method, if one exists.
   * Used by autocomplete to offer context-aware completions inside function arguments.
   */
  getParamHint(
    namespace: string,
    methodName: string,
    paramIndex: number,
    _language: 'python' | 'javascript'
  ): ParamHint | null {
    const method = this.getMethodDetail(namespace, methodName);
    if (!method || paramIndex < 0 || paramIndex >= method.parameters.length) {
      return null;
    }
    return method.parameters[paramIndex].paramHint ?? null;
  }

  /** Return all namespace names for context detection. */
  getAllNamespaces(): string[] {
    const ref = this.cache();
    if (!ref) return [];
    return ref.namespaces.map((n) => n.name);
  }

  /**
   * All identifiers a namespace can be referenced by in step code — the
   * server name plus its JS alias (jsName) when present. Used to seed the
   * editor linter's known globals so no-undef doesn't flag host modules
   * like `ehq` or WASM modules. (EHQ-108)
   */
  getNamespaceIdentifiers(): string[] {
    const ref = this.cache();
    if (!ref) return [];
    const ids = new Set<string>();
    for (const n of ref.namespaces) {
      if (n.name) ids.add(n.name);
      if (n.jsName) ids.add(n.jsName);
    }
    return [...ids];
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /** Strip namespace prefix from server names (e.g. "ehq.saveFile" → "saveFile") */
  private stripNamespacePrefix(name: string): string {
    const i = name.indexOf('.');
    return i >= 0 ? name.slice(i + 1) : name;
  }

  private getCachedItems(language: 'python' | 'javascript'): AutocompleteItem[] {
    const ref = this.cache();
    if (!ref) return [];
    if (ref !== this.itemsCacheRef) {
      this.itemsCacheJs = null;
      this.itemsCachePy = null;
      this.itemsCacheRef = ref;
    }
    if (language === 'python') {
      this.itemsCachePy ??= this.buildAutocompleteItems(ref, 'python');
      return this.itemsCachePy;
    } else {
      this.itemsCacheJs ??= this.buildAutocompleteItems(ref, 'javascript');
      return this.itemsCacheJs;
    }
  }

  private buildAutocompleteItems(
    ref: ApiReference,
    language: 'python' | 'javascript'
  ): AutocompleteItem[] {
    const items: AutocompleteItem[] = [];

    for (const ns of ref.namespaces) {
      // For JS, use jsName alias if available (WASM modules: "demoArrow" instead of "demo_arrow")
      const nsName = (language === 'javascript' && ns.jsName) ? ns.jsName : ns.name;

      // Add namespace itself as a module completion
      items.push({
        text: nsName,
        fullName: nsName,
        namespace: nsName,
        kind: 'module',
        description: ns.description,
      });

      for (const method of ns.methods) {
        const rawName =
          language === 'python' ? method.pythonName : method.displayName;
        // Server data may include namespace prefix (e.g. "ehq.save_file").
        // Strip it so text is just the method name for completion insertion.
        const dotIdx = rawName.indexOf('.');
        const methodName = dotIdx >= 0 ? rawName.slice(dotIdx + 1) : rawName;
        items.push({
          text: methodName,
          fullName: `${nsName}.${methodName}`,
          namespace: nsName,
          kind: 'function',
          description: method.description,
          signature: method.signature,
          returnType: method.returnType,
          availability: method.availability,
          isAsync: method.isAsync,
          parameters: method.parameters,
        });
      }
    }

    return items;
  }

  /**
   * Fuzzy scoring function.
   *
   * - Exact prefix match         -> 100
   * - Case-insensitive prefix    ->  80
   * - Camel-case initials match  ->  60
   * - Subsequence match          ->  40
   * - No match                   ->   0 (excluded)
   */
  private fuzzyScore(query: string, target: string): number {
    if (!query) return 100; // Empty query matches everything (namespace selected)

    // Exact prefix
    if (target.startsWith(query)) return 100;

    // Case-insensitive prefix
    if (target.toLowerCase().startsWith(query.toLowerCase())) return 80;

    // Camel-case initials (e.g. "gTR" matches "getTableRows")
    if (this.matchesCamelCase(query, target)) return 60;

    // Subsequence match (characters appear in order, case-insensitive)
    if (this.isSubsequence(query.toLowerCase(), target.toLowerCase())) return 40;

    return 0;
  }

  /**
   * Check if `query` matches the camelCase initials of `target`.
   * E.g. "gTR" matches "getTableRows", "pNA" matches "putNumericArray".
   */
  private matchesCamelCase(query: string, target: string): boolean {
    // Extract uppercase boundaries from target
    const initials: string[] = [];
    if (target.length > 0) {
      initials.push(target[0]); // Always include first char
    }
    for (let i = 1; i < target.length; i++) {
      if (target[i] >= 'A' && target[i] <= 'Z') {
        initials.push(target[i]);
      }
    }

    if (query.length > initials.length) return false;

    for (let i = 0; i < query.length; i++) {
      if (query[i].toLowerCase() !== initials[i].toLowerCase()) return false;
    }
    return true;
  }

  /** Check if all characters in `query` appear in `target` in order. */
  private isSubsequence(query: string, target: string): boolean {
    let qi = 0;
    for (let ti = 0; ti < target.length && qi < query.length; ti++) {
      if (query[qi] === target[ti]) qi++;
    }
    return qi === query.length;
  }
}
